import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  useSyncVouchers,
  useSyncVoucherApprovals,
  fetchVoucherDocumentBlob,
} from "@/hooks/use-voucher-actions";
import { useHighlight } from "@/hooks/use-highlight";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import VoucherApprovalDialog from "./VoucherApprovalDialog";
import VoucherApprovalModal from "./VoucherApprovalModal";
import VoucherTable from "./VoucherTable";
import DocumentModal from "./DocumentModal";
import { VoucherTypeBadge } from "./VoucherTypeBadge";
import type { DBVoucher } from "@/lib/db-types";
import type { BudgetLineRow, VoucherAllocationRow } from "@/hooks/use-project-detail";
import { formatNOK, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import { useProjectPermission } from "@/hooks/use-project-permission";

interface BilagTabProps {
  projectId: string;
  vouchers: DBVoucher[];
  budgetLines: BudgetLineRow[];
  voucherAllocations: VoucherAllocationRow[];
}

function PendingVoucherWrapper({
  voucherId,
  children,
}: {
  voucherId: string;
  children: React.ReactNode;
}) {
  const ref = useHighlight<HTMLDivElement>(voucherId, "voucherId");
  return (
    <div ref={ref} className="border rounded-lg overflow-hidden">
      {children}
    </div>
  );
}

export default function BilagTab({ projectId, vouchers, budgetLines, voucherAllocations }: BilagTabProps) {
  const syncMut = useSyncVouchers();
  const syncApprovalsMut = useSyncVoucherApprovals();
  // VIB-722c: Sølvrev er level 1-godkjenning. Bare brukere med
  // approve_vouchers ser godkjenn/avvis-knappene. Admin/superadmin overstyrer.
  const canApproveVouchers = useProjectPermission(projectId, "approve_vouchers");
  // VIB-1226/VIB-1204: ny selvforsynt modal (med inline budsjettpanel) bak
  // flagg. Av: gamle inline-panelet (VoucherApprovalDialog) uendret under.
  // cleanupRef VIB-1226 fjerner den gamle stien + denne if-grenen når
  // rollout har stått på "all" en periode.
  const pmInboxApprovalFlag = useFeatureFlag("feature_pm_inbox_approval");
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBudgetLine, setSelectedBudgetLine] = useState<string>("");
  const [ekstraAmount, setEkstraAmount] = useState<string>("");
  const [approveComment, setApproveComment] = useState<string>("");
  const [rejectComment, setRejectComment] = useState("");
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [activeVoucherId, setActiveVoucherId] = useState<string | null>(null);
  const [activeMode, setActiveMode] = useState<"approve" | "reject">("approve");
  const [pdfModal, setPdfModal] = useState<{
    open: boolean;
    blobUrl: string | null;
    contentType: string;
    title: string;
    loading: boolean;
    error: string | null;
  }>({
    open: false,
    blobUrl: null,
    contentType: "application/pdf",
    title: "",
    loading: false,
    error: null,
  });

  // VIB-874: deep-link fra PM-innboksens «Godkjenn»-knapp
  // (?attest=<voucherId>) åpner godkjenn-formen for det bilaget direkte.
  // Engangs: fjern paramet etterpå så den ikke re-åpner ved re-render.
  useEffect(() => {
    const attestId = searchParams.get("attest");
    if (!attestId) return;
    if (pmInboxApprovalFlag) {
      setActiveVoucherId(attestId);
      setActiveMode("approve");
    } else {
      setApprovingId(attestId);
      setRejectingId(null);
    }
    setSearchParams(
      (prev) => {
        prev.delete("attest");
        return prev;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, pmInboxApprovalFlag]);

  // VIB-588: filtrer ut øreavrundinger fra OutgoingCreditNote (typisk
  // -0,25 kr fra fakturaavslutninger). De er ikke reelle bilag og forvirrer
  // bilagslisten.
  const visibleVouchers = vouchers.filter((v) => {
    if (v.voucher_type === "OutgoingCreditNote" && v.amount != null && Math.abs(v.amount) < 1) {
      return false;
    }
    return true;
  });
  const pendingApproval = visibleVouchers.filter((v) => v.status === "pending_approval");
  const forwardedToDl = visibleVouchers.filter((v) => v.status === "forwarded_to_dl");
  const approved = visibleVouchers.filter((v) => v.status === "approved");
  const rejected = visibleVouchers.filter((v) => v.status === "rejected");

  const openDocument = async (voucher: DBVoucher) => {
    const title = voucher.supplier_name ?? `Bilag #${voucher.voucher_no}`;
    setPdfModal({ open: true, blobUrl: null, contentType: "application/pdf", title, loading: true, error: null });
    try {
      const format = voucher.has_images ? "images" : voucher.has_ehf ? "ehf" : "pdf";
      const { blobUrl, contentType } = await fetchVoucherDocumentBlob(voucher.id, format);
      setPdfModal((prev) => ({ ...prev, blobUrl, contentType, loading: false }));
    } catch (err) {
      setPdfModal((prev) => ({ ...prev, loading: false, error: (err as Error).message }));
    }
  };

  const closePdfModal = () => {
    if (pdfModal.blobUrl) URL.revokeObjectURL(pdfModal.blobUrl);
    setPdfModal({ open: false, blobUrl: null, contentType: "application/pdf", title: "", loading: false, error: null });
  };

  return (
    <div className="space-y-4">
      {/* Sync row */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {pendingApproval.length} til godkjenning
          {forwardedToDl.length > 0 ? `, ${forwardedToDl.length} venter bokføring` : ""}
          , {approved.length} bokført
          {rejected.length > 0 ? `, ${rejected.length} avvist` : ""}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5"
            onClick={() => syncApprovalsMut.mutate(undefined)}
            disabled={syncApprovalsMut.isPending}
            title="Hent bilag som er sendt til Sølvrev for godkjenning"
          >
            <SrIcon name={syncApprovalsMut.isPending ? "refresh-cw" : "clipboard-check"} className={cn("w-3 h-3", syncApprovalsMut.isPending && "animate-spin")} />
            Hent godkjenningskø
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5"
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending}
            title="Hent og oppdater bokførte bilag fra PowerOffice"
          >
            <SrIcon name="refresh-cw" className={cn("w-3 h-3", syncMut.isPending && "animate-spin")} />
            Synk fra PO
          </Button>
        </div>
      </div>

      {vouchers.length === 0 ? (
        <div className="border-[1.5px] border-foreground bg-card p-6">
          <p className="text-sm text-muted-foreground">Ingen bilag hentet fra PowerOffice ennå. Trykk "Synk fra PO" for å hente.</p>
        </div>
      ) : (
        <>
          {/* Pending approval — bilag fra PO sin /VoucherApproval-kø */}
          {pendingApproval.length > 0 && (
            <CollapsibleCard
              title="Til godkjenning"
              icon={<div className="w-2 h-2 rounded-full bg-orange-500" />}
              headerExtra={
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                  {pendingApproval.length} bilag
                </span>
              }
              contentClassName="border-t p-4 space-y-3"
            >
                  {pendingApproval.map((v) => (
                    <PendingVoucherWrapper key={v.id} voucherId={v.id}>
                      <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{v.supplier_name ?? `Bilag #${v.voucher_no ?? v.po_voucher_id.slice(0, 8)}`}</span>
                            {v.voucher_no && <span className="text-xs text-muted-foreground">#{v.voucher_no}</span>}
                            <VoucherTypeBadge type={v.voucher_type} />
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{v.description ?? "Ingen beskrivelse"}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-sm tabular-nums">{formatNOK(v.amount)}</p>
                          <p className="text-[11px] text-muted-foreground">{formatDate(v.voucher_date)}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-primary gap-1"
                            title="Se bilag"
                            onClick={() => openDocument(v)}
                          >
                            <SrIcon name="file-text" className="w-3.5 h-3.5"  />
                            PDF
                          </Button>
                          {canApproveVouchers && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
                                title="Godkjenn og send til daglig leder"
                                onClick={() => {
                                  if (pmInboxApprovalFlag) {
                                    setActiveVoucherId(v.id);
                                    setActiveMode("approve");
                                    return;
                                  }
                                  setApprovingId(approvingId === v.id ? null : v.id);
                                  setRejectingId(null);
                                }}
                              >
                                <SrIcon name="check" className="w-4 h-4"  />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                title="Avvis"
                                onClick={() => {
                                  if (pmInboxApprovalFlag) {
                                    setActiveVoucherId(v.id);
                                    setActiveMode("reject");
                                    return;
                                  }
                                  setRejectingId(rejectingId === v.id ? null : v.id);
                                  setApprovingId(null);
                                }}
                              >
                                <SrIcon name="x" className="w-4 h-4"  />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {!pmInboxApprovalFlag && (
                        <VoucherApprovalDialog
                          voucherId={v.id}
                          voucher={v}
                          budgetLines={budgetLines}
                          projectId={projectId}
                          approvingId={approvingId}
                          selectedBudgetLine={selectedBudgetLine}
                          ekstraAmount={ekstraAmount}
                          approveComment={approveComment}
                          rejectingId={rejectingId}
                          rejectComment={rejectComment}
                          onSetApprovingId={setApprovingId}
                          onSetSelectedBudgetLine={setSelectedBudgetLine}
                          onSetEkstraAmount={setEkstraAmount}
                          onSetApproveComment={setApproveComment}
                          onSetRejectingId={setRejectingId}
                          onSetRejectComment={setRejectComment}
                        />
                      )}
                    </PendingVoucherWrapper>
                  ))}
            </CollapsibleCard>
          )}

          {/* Godkjent av Sølvrev — venter bokføring (DL/Level 2) i PO (VIB-1056) */}
          {forwardedToDl.length > 0 && (
            <CollapsibleCard
              title="Godkjent — venter bokføring"
              icon={<div className="w-2 h-2 rounded-full bg-blue-500" />}
              headerExtra={
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                  {forwardedToDl.length} bilag
                </span>
              }
              contentClassName="border-t p-4 space-y-2"
            >
                  {forwardedToDl.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm border rounded bg-blue-50/30"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{v.supplier_name ?? `Bilag #${v.voucher_no ?? v.po_voucher_id.slice(0, 8)}`}</span>
                          {v.voucher_no && (
                            <span className="text-xs text-muted-foreground">#{v.voucher_no}</span>
                          )}
                          <VoucherTypeBadge type={v.voucher_type} />
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {v.description ?? "Ingen beskrivelse"}
                        </p>
                        {v.forwarded_to_dl_at && (
                          <p className="text-[11px] text-blue-700/80 mt-0.5">
                            Godkjent {formatDate(v.forwarded_to_dl_at)}
                          </p>
                        )}
                      </div>
                      {/* Ingen PDF-link her (VIB-1248): når bilaget er videresendt
                          til DL er det ute av PO sin approval-kø, og VoucherApproval-
                          id-en gir 404. Dokumentet kan sjekkes i «Til godkjenning»
                          før videresending, og igjen når bilaget er bokført. */}
                      <span className="tabular-nums font-medium ml-2 shrink-0">
                        {formatNOK(v.amount)}
                      </span>
                    </div>
                  ))}
            </CollapsibleCard>
          )}

          {/* Bokført i PO (Flow 2 bekreftet) — VIB-1056 */}
          {approved.length > 0 && (
            <CollapsibleCard
              title="Bokført"
              icon={<div className="w-2 h-2 rounded-full bg-green-500" />}
              count={approved.length}
              headerExtra={
                <span className="text-sm text-muted-foreground ml-2">
                  {formatNOK(approved.reduce((s, v) => s + (v.amount ?? 0), 0))} totalt
                </span>
              }
              contentClassName=""
            >
              <VoucherTable
                vouchers={approved}
                budgetLines={budgetLines}
                voucherAllocations={voucherAllocations}
                projectId={projectId}
                onOpenDocument={openDocument}
                canAllocate={canApproveVouchers}
              />
            </CollapsibleCard>
          )}

          {/* Rejected vouchers (collapsed) */}
          {rejected.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 py-2">
                <SrIcon name="chevron-down" className="w-3 h-3 transition-transform group-open:rotate-180"  />
                {rejected.length} avviste bilag
              </summary>
              <div className="border-[1.5px] border-foreground bg-card mt-1 p-4">
                <div className="space-y-2">
                  {rejected.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center justify-between px-3 py-2 text-sm border rounded bg-red-50/30"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{v.supplier_name ?? `#${v.voucher_no ?? v.po_voucher_id.slice(0, 8)}`}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{v.description}</span>
                      </div>
                      <span className="tabular-nums font-medium ml-3">{formatNOK(v.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          )}
        </>
      )}

      {/* Document Viewer Modal */}
      <DocumentModal modal={pdfModal} onClose={closePdfModal} />

      {pmInboxApprovalFlag && (
        <VoucherApprovalModal
          voucherId={activeVoucherId}
          projectId={projectId}
          mode={activeMode}
          open={!!activeVoucherId}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setActiveVoucherId(null);
          }}
        />
      )}
    </div>
  );
}
